import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { X, Plus, Check } from "lucide-react";

interface EditableTextProps {
  value: string;
  onSave: (value: string) => void;
  placeholder?: string;
  className?: string;
  multiline?: boolean;
  disabled?: boolean;
}

export const EditableText: React.FC<EditableTextProps> = ({
  value,
  onSave,
  placeholder = "",
  className = "",
  multiline = false,
  disabled = false,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleSave = () => {
    onSave(localValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setLocalValue(value);
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (disabled || !isEditing) {
    return (
      <div
        className={`cursor-pointer hover:bg-gray-50 p-2 rounded-md transition-colors ${className}`}
        onClick={() => !disabled && setIsEditing(true)}
      >
        {value || <span className="text-gray-400 italic">{placeholder}</span>}
        {!disabled && (
          <span className="text-xs text-gray-400 ml-2">(انقر للتعديل)</span>
        )}
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {multiline ? (
        <Textarea
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder={placeholder}
          autoFocus
          rows={3}
        />
      ) : (
        <Input
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder={placeholder}
          autoFocus
        />
      )}
      <div className="flex gap-2">
        <Button size="sm" onClick={handleSave}>
          <Check className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={handleCancel}>
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

interface EditableToggleProps {
  value: boolean;
  onSave: (value: boolean) => void;
  label: string;
  disabled?: boolean;
}

export const EditableToggle: React.FC<EditableToggleProps> = ({
  value,
  onSave,
  label,
  disabled = false,
}) => {
  return (
    <div className="flex items-center space-x-2">
      <Switch
        checked={value}
        onCheckedChange={onSave}
        disabled={disabled}
      />
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
};

interface EditableTagsProps {
  tags: string[];
  onSave: (tags: string[]) => void;
  disabled?: boolean;
}

export const EditableTags: React.FC<EditableTagsProps> = ({
  tags,
  onSave,
  disabled = false,
}) => {
  const [localTags, setLocalTags] = useState(tags);
  const [newTag, setNewTag] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    setLocalTags(tags);
  }, [tags]);

  const addTag = () => {
    if (newTag.trim() && !localTags.includes(newTag.trim())) {
      const updatedTags = [...localTags, newTag.trim()];
      setLocalTags(updatedTags);
      onSave(updatedTags);
      setNewTag("");
      setIsAdding(false);
    }
  };

  const removeTag = (tagToRemove: string) => {
    const updatedTags = localTags.filter(tag => tag !== tagToRemove);
    setLocalTags(updatedTags);
    onSave(updatedTags);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      addTag();
    } else if (e.key === 'Escape') {
      setNewTag("");
      setIsAdding(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {localTags.map((tag, index) => (
          <Badge
            key={index}
            variant="secondary"
            className="flex items-center gap-1 bg-orange-50 text-orange-700 border-orange-200"
          >
            #{tag}
            {!disabled && (
              <X
                className="w-3 h-3 cursor-pointer hover:text-red-500"
                onClick={() => removeTag(tag)}
              />
            )}
          </Badge>
        ))}
        
        {!disabled && (
          <>
            {isAdding ? (
              <div className="flex items-center gap-1">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="إضافة علامة جديدة"
                  className="w-32 h-8 text-xs"
                  autoFocus
                />
                <Button size="sm" onClick={addTag} className="h-8 w-8 p-0">
                  <Check className="w-3 h-3" />
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => { setNewTag(""); setIsAdding(false); }}
                  className="h-8 w-8 p-0"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsAdding(true)}
                className="h-8 flex items-center gap-1 text-xs"
              >
                <Plus className="w-3 h-3" />
                إضافة علامة
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

interface EditablePlatformProps {
  platforms: {
    facebook?: string;
    instagram?: string;
    whatsapp?: string;
    website?: string;
    phone?: string;
  };
  onSave: (platforms: {
    facebook?: string;
    instagram?: string;
    whatsapp?: string;
    website?: string;
    phone?: string;
  }) => void;
  disabled?: boolean;
}

export const EditablePlatform: React.FC<EditablePlatformProps> = ({
  platforms,
  onSave,
  disabled = false,
}) => {
  const [localPlatforms, setLocalPlatforms] = useState(platforms);

  useEffect(() => {
    setLocalPlatforms(platforms);
  }, [platforms]);

  const updatePlatform = (platform: string, value: string) => {
    const updated = { ...localPlatforms, [platform]: value };
    setLocalPlatforms(updated);
    onSave(updated);
  };

  const platformLabels = {
    facebook: "Facebook",
    instagram: "Instagram",
    whatsapp: "WhatsApp",
    website: "الموقع الإلكتروني",
    phone: "رقم الهاتف",
  };

  return (
    <div className="space-y-3">
      {Object.entries(platformLabels).map(([key, label]) => (
        <div key={key}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
          {disabled ? (
            <div className="p-2 bg-gray-50 rounded-md text-sm">
              {localPlatforms[key as keyof typeof localPlatforms] || "غير محدد"}
            </div>
          ) : (
            <Input
              value={localPlatforms[key as keyof typeof localPlatforms] || ""}
              onChange={(e) => updatePlatform(key, e.target.value)}
              placeholder={`رابط ${label}`}
              type={key === "phone" ? "tel" : key === "website" ? "url" : "text"}
            />
          )}
        </div>
      ))}
    </div>
  );
};

interface EditableNumberProps {
  value: number;
  onSave: (value: number) => void;
  label: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  helperText?: string;
}

export const EditableNumber: React.FC<EditableNumberProps> = ({
  value,
  onSave,
  label,
  min = 0,
  max = 10,
  step = 0.1,
  disabled = false,
  helperText,
}) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value) || 0;
    setLocalValue(newValue);
    onSave(newValue);
  };

  const getColorClass = (val: number) => {
    if (val >= 7) return "text-green-600";
    if (val >= 4) return "text-yellow-600";
    return "text-gray-400";
  };

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        <span className={`ml-2 font-bold ${getColorClass(localValue)}`}>
          ({localValue}/{max})
        </span>
      </label>
      <Input
        type="number"
        min={min}
        max={max}
        step={step}
        value={localValue}
        onChange={handleChange}
        disabled={disabled}
        className="w-full"
      />
      {helperText && (
        <p className="text-xs text-gray-500">{helperText}</p>
      )}
    </div>
  );
};